<template>
    <nav class="flex items-center flex-wrap -mx-2 mb-8">
        <label for="projects-year" class="sr-only">Year</label>
        <select id="projects-year" v-model="year" title="Year" class="form-select inline-block w-auto mx-2">
            <option :value="null">Year</option>
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>

        <label for="projects-sector" class="sr-only">Sector</label>
        <select id="projects-sector" v-model="sector" title="Sector" class="form-select inline-block w-auto mx-2">
            <option :value="null">Sector</option>
            <option v-for="s in sectors" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>

        <div class="w-full md:w-auto md:flex md:flex-1 justify-end mx-2 mt-4 md:mt-0">
            <label for="projects-search" class="sr-only">Search</label>

            <div class="relative">
                <input
                    id="projects-search"
                    v-model="search"
                    title="Search"
                    type="search"
                    placeholder="Search"
                    class="block pl-10 pr-4 py-2 max-md:w-full rounded-full font-normal placeholder-black bg-white text-black appearance-none border border-black focus:outline-none text-sm lg:text-base"
                />

                <search-icon class="absolute block w-4 h-auto left-4 align-y" />
            </div>
        </div>
    </nav>

    <div
        v-infinite-scroll="onLoadMore"
        class="grid grid-cols-1 lg:grid-cols-3 grid-flow-row auto-rows-max gap-4 lg:gap-8"
        infinite-scroll-disabled="disableLazyLoad"
        infinite-scroll-distance="10"
        infinite-scroll-immediate-check="false"
    >
        <project-item
            v-for="(project, index) in projects"
            :key="project.id"
            :index="index"
            :project="project"
        />
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import { useQuery } from '@vue/apollo-composable';
    import ProjectItem from '@/js/components/ProjectItem.vue';
    import { GET_PROJECTS_QUERY } from '@/graphql/projects';
    import SearchIcon from '@/img/icons/magnifying-glass-regular.svg?component';

    const props = defineProps({
        sectors: {
            type: Array,
            required: true,
        },
    });

    const page = ref(0);
    const search = ref('');
    const year = ref(null);
    const sector = ref(null);
    const disableLazyLoad = ref(true);

    const entriesPerPage = 20;

    const years = computed(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 11 }, (_, index) => `${currentYear - index}`);
    });

    const { result, fetchMore } = useQuery(GET_PROJECTS_QUERY, () => ({
        limit: entriesPerPage,
        offset: 0,
        year: year.value ? [ year.value ] : undefined,
        sector: sector.value ? [ sector.value ] : undefined,
        search: search.value.trim() || undefined,
    }), {
        keepPreviousResult: true,
    });

    const projects = computed(() => result.value?.entries || []);

    const onLoadMore = async() => {
        page.value++;
        const offset = page.value * entriesPerPage;

        await fetchMore({
            variables: {
                limit: entriesPerPage,
                offset,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const entries = [
                    ...previousResult.entries,
                    ...fetchMoreResult.entries,
                ];

                disableLazyLoad.value = fetchMoreResult.entries.length < entriesPerPage;

                return {
                    entries,
                };
            },
        });
    };
</script>
